import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  isAuthSessionInvalidError,
} from "@/lib/authStorage";
import { extractOAuthTokenFromUrl, isOAuthCallbackPath } from "@/lib/oauthCallbackToken";

const AuthContext = createContext(null);

async function fetchMeOnce() {
  const res = await api.get("/auth/me");
  return res.data;
}

/** Átmeneti hiba esetén egy újrapróba (Safari háttérből vissza, gyenge hálózat). */
async function fetchMeWithRetry() {
  try {
    return await fetchMeOnce();
  } catch (e) {
    if (isAuthSessionInvalidError(e)) throw e;
    await new Promise((r) => setTimeout(r, 800));
    return fetchMeOnce();
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const data = await fetchMeWithRetry();
      setUser(data);
      return data;
    } catch (e) {
      if (isAuthSessionInvalidError(e)) {
        removeAuthToken();
        setUser(null);
      }
      throw e;
    }
  }, []);

  const bootstrapSession = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const data = await fetchMeWithRetry();
      setUser(data);
      const tok = getAuthToken();
      if (tok) setAuthToken(tok);
    } catch (e) {
      if (isAuthSessionInvalidError(e)) {
        removeAuthToken();
        setUser(null);
      }
      /* hálózati hiba: token marad, user lehet stale — ne dobjuk ki a fiókból */
    }
  }, []);

  useEffect(() => {
    const path = window.location.pathname || "";
    const pendingOAuth =
      isOAuthCallbackPath(path)
      || window.location.hash?.includes("session_id=")
      || window.location.hash?.includes("auth_token=")
      || !!extractOAuthTokenFromUrl();
    if (pendingOAuth) {
      setLoading(false);
      return;
    }
    bootstrapSession().finally(() => setLoading(false));
  }, [bootstrapSession]);

  useEffect(() => {
    const onPageShow = (ev) => {
      if (!getAuthToken()) return;
      if (ev.persisted) bootstrapSession();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [bootstrapSession]);

  const persist = (data) => {
    setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const login = async (email, password) => persist((await api.post("/auth/login", { email, password })).data);
  const register = async (name, email, password, referral_code, message_email_consent, signup_promo, seller_type = "private") =>
    persist(
      (
        await api.post("/auth/register", {
          name,
          email,
          password,
          referral_code,
          signup_promo: signup_promo || undefined,
          seller_type: seller_type === "dealer" ? "dealer" : "private",
          message_email_consent: !!message_email_consent,
        })
      ).data,
    );
  const googleLogin = async (session_id) => persist((await api.post("/auth/google", { session_id })).data);

  const completeGoogleLogin = async (token) => {
    setAuthToken(token);
    const r = await api.get("/auth/me");
    setUser(r.data);
    return r.data;
  };
  const updateProfile = async (data) => {
    const r = await api.put("/auth/profile", data);
    setUser(r.data);
    return r.data;
  };
  const logout = () => {
    removeAuthToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleLogin, completeGoogleLogin, updateProfile, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
